import React, { useState, useRef, useCallback } from 'react';
import TotpModal from 'components/TotpModal';
import { post } from 'services/ApiService';
import { useUser } from 'context/UserContext';

interface TotpState {
  isOpen: boolean;
  step: 1 | 2;
  code: string;
  loading: boolean;
  error: string | null;
  operation: string;
}

const initialState: TotpState = {
  isOpen: false,
  step: 1,
  code: '',
  loading: false,
  error: null,
  operation: '',
};

export function useTotp() {
  const [state, setState] = useState<TotpState>(initialState);
  const resolveRef = useRef<((code: string | null) => void) | null>(null);
  const { getId } = useUser();

  const reset = () => {
    setState(initialState);
    resolveRef.current = null;
  };

  const requestTotp = useCallback((operation: string): Promise<string | null> => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setState({
        ...initialState,
        isOpen: true,
        operation,
      });
    });
  }, []);

  const handleSendCode = async () => {
    const matriculateId = getId();
    if (!matriculateId) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const res = await post(`matriculates/${matriculateId}/totp`, {
        operation: state.operation,
      });

      if (res.status === 'ok') {
        setState(prev => ({ ...prev, step: 2, loading: false, code: '' }));
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: res.message || 'Error al enviar el código de verificación',
        }));
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Error de conexión. Intentá nuevamente.',
      }));
    }
  };

  const handleCodeChange = (code: string) => {
    setState(prev => ({ ...prev, code, error: null }));
  };

  const handleConfirm = () => {
    if (state.code.length === 6 && resolveRef.current) {
      resolveRef.current(state.code);
      reset();
    }
  };

  const handleCancel = () => {
    if (resolveRef.current) {
      resolveRef.current(null);
    }
    reset();
  };

  const handleResend = () => {
    setState(prev => ({ ...prev, code: '', error: null }));
    handleSendCode();
  };

  const totpModalElement = (
    <TotpModal
      isOpen={state.isOpen}
      step={state.step}
      code={state.code}
      loading={state.loading}
      error={state.error}
      onSendCode={handleSendCode}
      onCodeChange={handleCodeChange}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      onResend={handleResend}
    />
  );

  return {
    requestTotp,
    TotpModal: totpModalElement,
  };
}
